import { makeStyles, mergeClasses, SkeletonItem } from '@fluentui/react-components';
import { OpenInNew } from '@material-symbols-svg/react/rounded/w500';
import type { ReactNode } from 'react';

import { formatHM } from '../common';
import { palette, tokens, useAppButtonStyles } from '../theme';
import { useWorkItemDetails } from './use-work-item-details';
import { WorkItemAssignee } from './WorkItemAssignee';
import { workItemTypeAccent } from './workItemColors';
import { WorkItemStateDot } from './WorkItemStateDot';
import { WorkItemTypeBadge } from './WorkItemTypeBadge';

// "C2D\Oct 2026" reads as "Oct 2026".
const lastSegment = (path: string): string => path.split('\\').at(-1) ?? path;

export type WorkItemPreviewCardProps = {
    workItemId: number;
    // Known from the row, so the card shows them before the details arrive.
    title: string;
    workItemType: string;
};

// Laid out like the ADO work item header: type, id and title, assignee, then the fields band.
export const WorkItemPreviewCard: React.FC<WorkItemPreviewCardProps> = ({ workItemId, title, workItemType }) => {
    const classes = useStyles();
    const buttons = useAppButtonStyles();
    const details = useWorkItemDetails(workItemId);
    const loaded = details.status === 'success' ? details.data : null;
    const failed = details.status === 'error';

    return (
        <article className={classes.card} aria-label={`Work item #${workItemId}`} aria-busy={details.status === 'loading'}>
            <header className={classes.header} style={{ borderLeftColor: workItemTypeAccent(workItemType, title) }}>
                <WorkItemTypeBadge workItemType={workItemType} title={title} />
                <h3 className={classes.title}>
                    <span className={classes.id}>#{workItemId}</span>
                    {loaded?.title ?? title}
                </h3>
                {loaded && (
                    <span className={classes.assignee}>
                        <WorkItemAssignee email={loaded.assignedToEmail} name={loaded.assignedToName} size={24} />
                        {loaded.assignedToName ?? 'Unassigned'}
                    </span>
                )}
                {!loaded && !failed && <SkeletonItem size={24} className={classes.skeletonAssignee} />}
            </header>

            {failed ? (
                <p className={classes.error}>Could not load the details from Azure DevOps.</p>
            ) : (
                <>
                    <dl className={classes.fields}>
                        <Fact
                            label="State"
                            value={
                                loaded ? (
                                    <span className={classes.state}>
                                        <WorkItemStateDot state={loaded.state} />
                                        {loaded.state}
                                    </span>
                                ) : null
                            }
                        />
                        <Fact label="Iteration" value={loaded ? lastSegment(loaded.iterationPath) || '–' : null} />
                        <Fact label="Work type" value={loaded ? (loaded.workType ?? '–') : null} />
                        <Fact label="Logged" value={loaded ? `${formatHM(loaded.completedWork)} h` : null} />
                    </dl>

                    <section className={classes.description} aria-label="Description">
                        {loaded ? (
                            <p className={mergeClasses(classes.descriptionText, !loaded.description && classes.muted)}>
                                {loaded.description || 'No description.'}
                            </p>
                        ) : (
                            <>
                                <SkeletonItem size={12} className={classes.skeletonLine} />
                                <SkeletonItem size={12} className={classes.skeletonLine} />
                                <SkeletonItem size={12} className={classes.skeletonShortLine} />
                            </>
                        )}
                    </section>
                </>
            )}

            {loaded && (
                <footer className={classes.footer}>
                    <a
                        href={loaded.webUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={mergeClasses(buttons.link, classes.openLink)}
                    >
                        Open in Azure DevOps
                        <OpenInNew aria-hidden />
                    </a>
                </footer>
            )}
        </article>
    );
};

type FactProps = {
    label: string;
    // null while the details load.
    value: ReactNode;
};

const Fact: React.FC<FactProps> = ({ label, value }) => {
    const classes = useStyles();
    return (
        <>
            <dt className={classes.factLabel}>{label}</dt>
            <dd className={classes.factValue}>{value ?? <SkeletonItem size={12} className={classes.skeletonValue} />}</dd>
        </>
    );
};

const useStyles = makeStyles({
    card: {
        width: '480px',
        maxWidth: 'calc(100vw - 32px)',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '12px',
        boxSizing: 'border-box',
    },
    // The type-coloured bar runs down the header only, as in ADO.
    header: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: '8px',
        padding: '14px 16px 12px 14px',
        borderLeft: '4px solid',
        borderTopLeftRadius: '12px',
    },
    title: {
        margin: 0,
        fontSize: '16px',
        fontWeight: tokens.fontWeightSemibold,
        lineHeight: 1.35,
        color: tokens.colorNeutralForeground1,
        overflowWrap: 'anywhere',
    },
    id: {
        marginRight: '8px',
        fontVariantNumeric: 'tabular-nums',
    },
    assignee: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        fontSize: '13px',
        color: tokens.colorNeutralForeground1,
    },
    // Two fields per row: label, value, label, value.
    fields: {
        display: 'grid',
        gridTemplateColumns: 'auto minmax(0, 1fr) auto minmax(0, 1fr)',
        columnGap: '12px',
        rowGap: '8px',
        margin: 0,
        padding: '12px 16px',
        backgroundColor: palette.panelMuted,
        fontSize: '12.5px',
    },
    factLabel: {
        color: palette.dimText,
        whiteSpace: 'nowrap',
    },
    factValue: {
        margin: 0,
        minWidth: 0,
        display: 'flex',
        alignItems: 'center',
        minHeight: '18px',
        color: tokens.colorNeutralForeground1,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    state: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
    },
    description: {
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
        padding: '12px 16px 0',
    },
    descriptionText: {
        margin: 0,
        fontSize: '12.5px',
        lineHeight: 1.5,
        color: tokens.colorNeutralForeground2,
        whiteSpace: 'pre-line',
        overflowWrap: 'anywhere',
        display: '-webkit-box',
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: 6,
        overflow: 'hidden',
    },
    muted: {
        color: palette.dimText,
        fontStyle: 'italic',
    },
    error: {
        margin: 0,
        padding: '12px 16px 14px',
        fontSize: '12.5px',
        color: palette.overText,
    },
    footer: {
        display: 'flex',
        justifyContent: 'flex-end',
        padding: '4px 12px 10px',
    },
    openLink: {
        textDecorationLine: 'none',
    },
    skeletonAssignee: {
        width: '160px',
    },
    skeletonValue: {
        width: '60%',
    },
    skeletonLine: {
        width: '100%',
    },
    skeletonShortLine: {
        width: '55%',
    },
});
